import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Phase P.26 — mandatory-address modal for legacy tenants. Rendered by
// `Layout.jsx` on every authenticated route so a tenant that predates
// P.26 gets a one-time forcing function to fill in their VAT-compliance
// address the moment they next log in. Cannot be dismissed until the
// four mandatory fields are saved.
const COUNTRIES = [
  { code: "IE", name: "Ireland" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "AT", name: "Austria" }, { code: "BE", name: "Belgium" },
  { code: "BG", name: "Bulgaria" }, { code: "HR", name: "Croatia" },
  { code: "CY", name: "Cyprus" }, { code: "CZ", name: "Czechia" },
  { code: "DK", name: "Denmark" }, { code: "EE", name: "Estonia" },
  { code: "FI", name: "Finland" }, { code: "FR", name: "France" },
  { code: "DE", name: "Germany" }, { code: "GR", name: "Greece" },
  { code: "HU", name: "Hungary" }, { code: "IT", name: "Italy" },
  { code: "LV", name: "Latvia" }, { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" }, { code: "MT", name: "Malta" },
  { code: "NL", name: "Netherlands" }, { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" }, { code: "RO", name: "Romania" },
  { code: "SK", name: "Slovakia" }, { code: "SI", name: "Slovenia" },
  { code: "ES", name: "Spain" }, { code: "SE", name: "Sweden" },
];

export default function BillingAddressRequiredModal() {
  const { manager, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    country: "IE", address_line1: "", city: "", postal_code: "", vat_number: "",
  });
  const [companyExtras, setCompanyExtras] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (!manager) return;
    // Server-computed flag: false when country/line1/city/postal_code
    // aren't all populated on settings.company. Fresh signups arrive with
    // this already true so they never see this modal.
    if (manager.company_address_complete === false) {
      setOpen(true);
    }
  }, [manager]);

  // Prefill from any partial data already on the tenant so the user doesn't
  // retype fields they've already filled elsewhere.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        const c = data?.company || {};
        setForm((f) => ({
          country: c.country || f.country || "IE",
          address_line1: c.address_line1 || "",
          city: c.city || "",
          postal_code: c.postal_code || "",
          vat_number: c.vat_number || "",
        }));
        // Preserve any other company fields (name, phone, logo, branding)
        // when we PUT back — /settings expects the FULL company object.
        setCompanyExtras(c);
      } catch (_e) { /* modal still usable without prefill */ }
    })();
  }, [open]);

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setError("");
    const country = (form.country || "").trim().toUpperCase();
    const address_line1 = (form.address_line1 || "").trim();
    const city = (form.city || "").trim();
    const postal_code = (form.postal_code || "").trim();
    if (country.length !== 2) { setError("Country must be a 2-letter ISO code."); return; }
    if (!address_line1) { setError("Address line 1 is required."); return; }
    if (!city) { setError("City is required."); return; }
    if (!postal_code) { setError("Postal code is required."); return; }
    setBusy(true);
    try {
      await api.put("/settings", {
        company: {
          ...companyExtras,
          country,
          address_line1,
          city,
          postal_code,
          vat_number: (form.vat_number || "").trim(),
        },
      });
      toast.success("Business address saved");
      setOpen(false);
      if (typeof refresh === "function") await refresh();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="billing-address-required-modal"
    >
      <div className="bg-card border border-border rounded-[10px] max-w-lg w-full p-5 space-y-4 shadow-2xl">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-accent">// Action required</div>
          <h3 className="text-lg font-extrabold tracking-tight mt-1" data-testid="billing-address-required-title">
            Please complete your business address
          </h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            To comply with tax requirements on subscription invoices we need
            your registered business address. Takes 20 seconds — this only
            appears once.
          </p>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="p26-country">Country</Label>
              <select
                id="p26-country"
                required
                value={form.country}
                onChange={on("country")}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="billing-address-country-select"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="p26-postal">Postal code</Label>
              <Input id="p26-postal" required value={form.postal_code} onChange={on("postal_code")} placeholder="D02 XY45" data-testid="billing-address-postal-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="p26-line1">Address line 1</Label>
            <Input id="p26-line1" required value={form.address_line1} onChange={on("address_line1")} placeholder="24 Main Street" data-testid="billing-address-line1-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="p26-city">City</Label>
              <Input id="p26-city" required value={form.city} onChange={on("city")} placeholder="Dublin" data-testid="billing-address-city-input" />
            </div>
            <div>
              <Label htmlFor="p26-vat">VAT number <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="p26-vat" value={form.vat_number} onChange={on("vat_number")} placeholder="IE1234567X" data-testid="billing-address-vat-input" />
            </div>
          </div>
          {error && <div className="text-sm text-destructive" data-testid="billing-address-error">{error}</div>}
        </div>
        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={busy}
            className="btn-cta"
            data-testid="billing-address-save-btn"
          >
            {busy ? "Saving…" : "Save and continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
